import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Zap, Phone, Star, Clock, Users } from 'lucide-react';
import ScrollReveal from '../effects/ScrollReveal';

const perks = [
  { icon: Star, text: 'Industry-recognized certification' },
  { icon: Users, text: '5,000+ alumni network' },
  { icon: Clock, text: 'Placement support till you\'re hired' },
  { icon: Zap, text: 'Start learning in 48 hours' },
];

export default function FinalCTA() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['5%', '-5%']);

  return (
    <section ref={ref} className="py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <ScrollReveal variant="zoomIn">
          <div className="relative rounded-3xl overflow-hidden min-h-[440px] flex items-center">
            {/* Parallax background */}
            <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1800&q=80"
                alt="cta background"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b bg-gradient-to-t from-black/100 to-transparent" />
            </motion.div>
            <div className="absolute inset-0 bg-foreground/93" />

            {/* Grid overlay */}
            <div className="absolute inset-0 opacity-[0.08]"
              style={{ backgroundImage: 'linear-gradient(white 1px,transparent 1px),linear-gradient(90deg,white 1px,transparent 1px)', backgroundSize: '44px 44px' }} />

            {/* Floating orbs */}
            <motion.div
              animate={{ scale: [1, 1.4, 1], x: [0, 30, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute top-[-10%] right-[15%] w-[350px] h-[350px] rounded-full opacity-15 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 65%)' }}
            />
            <motion.div
              animate={{ scale: [1.2, 1, 1.2], x: [0, -20, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
              className="absolute bottom-[-15%] left-[10%] w-[300px] h-[300px] rounded-full opacity-10 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 65%)' }}
            />

            {/* Floating particles */}
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full"
                style={{
                  width: 3 + (i % 3) * 2,
                  height: 3 + (i % 3) * 2,
                  left: `${8 + i * 9}%`,
                  top: `${12 + (i % 4) * 22}%`,
                  backgroundColor: i % 3 === 0 ? 'rgba(254,210,2,0.5)' : 'rgba(255,255,255,0.2)',
                }}
                animate={{ y: [-15, 15, -15], opacity: [0.2, 0.7, 0.2], scale: [1, 1.4, 1] }}
                transition={{ duration: 2.5 + i * 0.4, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}

            <motion.div style={{ y: textY }} className="relative z-10 px-8 sm:px-14 py-14 text-center w-full">
              {/* Urgency badge */}
             

              <motion.h2
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] max-w-3xl mx-auto"
              >
                Your Career Transformation
                <br />
                <span className="text-primary">Starts Right Here.</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-white/65 mt-5 max-w-lg mx-auto text-lg leading-relaxed font-body"
              >
                Join thousands of students who have already transformed their careers with XceedEdge.
              </motion.p>

              {/* Perks */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-wrap justify-center gap-3 mt-8"
              >
                {perks.map((perk, i) => {
                  const PerkIcon = perk.icon;
                  return (
                    <motion.span
                      key={perk.text}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.35 + i * 0.08 }}
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 text-white/85 text-xs font-semibold"
                    >
                      <PerkIcon className="w-3.5 h-3.5 text-primary" />
                      {perk.text}
                    </motion.span>
                  );
                })}
              </motion.div>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-10"
              >
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.07, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center gap-2.5 px-9 py-4 rounded-2xl bg-primary text-primary-foreground font-bold text-base shadow-2xl shadow-primary/40 hover:bg-primary/90 transition-colors"
                  >
                    Book Free Demo
                    <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>
                      <ArrowRight className="w-5 h-5" />
                    </motion.span>
                  </motion.button>
                </Link>
                <Link to="/courses">
                  <motion.button
                    whileHover={{ scale: 1.07, y: -3 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center gap-2.5 px-9 py-4 rounded-2xl border border-white/25 text-white font-semibold text-base hover:bg-white/10 transition-colors backdrop-blur-sm"
                  >
                    <Phone className="w-4 h-4" />
                    Explore Programs
                  </motion.button>
                </Link>
              </motion.div>

            
            </motion.div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}